import React, { useEffect, useState } from "react";
import { api, errorMessage, fileUrl } from "../api/client";
import type { AiModel, EvaluationItem, ModelEvaluation } from "../api/types";
import { Card, cx } from "../components/Controls";
import { Badge } from "../components/Badge";
import { ImageLightbox } from "../components/ImageLightbox";
import { fmtNum } from "../lib/format";

function itemTag(it: EvaluationItem): { text: string; tone: "ok" | "ng" | "muted" } {
  if (it.label === "NG") return it.predicted === "NG" ? { text: "Detectada", tone: "ok" } : { text: "No detectada", tone: "ng" };
  return it.predicted === "NG" ? { text: "Falso NG", tone: "ng" } : { text: "OK", tone: "muted" };
}

function Section({ title, items, onOpen }: { title: string; items: EvaluationItem[]; onOpen: (src: string | null) => void }) {
  if (!items.length) return null;
  return (
    <div className="mt-5">
      <div className="mb-2 text-sm font-medium text-slate-700">{title}</div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3 sm:grid-cols-[repeat(auto-fill,160px)]">
        {items.map((it) => {
          const tag = itemTag(it);
          const src = fileUrl(it.heatmap_url) || fileUrl(it.thumb_url);
          return (
            <button
              key={it.image_id}
              type="button"
              onClick={() => onOpen(fileUrl(it.heatmap_url) || fileUrl(it.image_url))}
              className={cx("relative overflow-hidden rounded-md border-2 bg-slate-900 text-left", it.correct ? "border-transparent" : "border-ng")}
              data-testid="evaluation-item"
              data-label={it.label}
              data-predicted={it.predicted}
            >
              {src ? <img src={src} alt="" className="aspect-[4/3] w-full object-cover" loading="lazy" /> : <div className="aspect-[4/3] w-full" />}
              <span className="absolute left-1 top-1"><Badge tone={tag.tone}>{tag.text}</Badge></span>
              <span className="absolute bottom-1 left-1 rounded bg-ink/80 px-1.5 py-0.5 font-mono text-[10px] text-white">
                {it.point_index === null ? "" : `P${it.point_index + 1} · `}
                {fmtNum(it.score)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ModelEvaluationCard({ model }: { model: AiModel }) {
  const [data, setData] = useState<ModelEvaluation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .get<ModelEvaluation>(`/models/${model.id}/evaluation`)
      .then((r) => {
        if (alive) setData(r);
      })
      .catch((e) => {
        if (alive) setError(errorMessage(e));
      });
    return () => {
      alive = false;
    };
  }, [model.id, model.threshold]);

  if (error) return <Card className="p-5 text-sm text-ng" data-testid="model-evaluation-error">{error}</Card>;
  if (!data) return null;
  if (!data.items.length) {
    return (
      <Card className="p-5 text-sm text-slate-600" data-testid="model-evaluation-card" data-empty="true">
        <h3 className="mb-1 font-display text-base font-semibold text-ink">Prueba del modelo, foto por foto</h3>
        Este modelo se entrenó antes de que existiera la prueba foto por foto. Al volver a entrenarlo aparece acá.
      </Card>
    );
  }
  const ng = data.items.filter((i) => i.label === "NG");
  const ok = data.items.filter((i) => i.label === "OK");
  return (
    <Card className="p-5" data-testid="model-evaluation-card">
      <h3 className="font-display text-base font-semibold">Prueba del modelo, foto por foto</h3>
      <p className="mt-1 text-xs text-slate-500">
        Con el umbral actual ({fmtNum(data.threshold)}). Las fotos OK de esta prueba se apartaron del entrenamiento: el modelo no las había visto.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-md bg-slate-50 p-3" data-testid="evaluation-ng-summary">
          <div className="font-display text-2xl font-semibold">{data.ng_total ? `${data.ng_detected} de ${data.ng_total}` : "—"}</div>
          <div className="text-xs text-slate-500">
            {data.ng_total ? "fotos NG detectadas (en rojo, la zona de la falla)" : "Sin fotos NG: subir fotos de la pieza con defectos reales para medir la detección."}
          </div>
        </div>
        <div className="rounded-md bg-slate-50 p-3" data-testid="evaluation-ok-summary">
          <div className="font-display text-2xl font-semibold">{data.ok_total ? `${data.ok_false_ng} de ${data.ok_total}` : "—"}</div>
          <div className="text-xs text-slate-500">
            {data.ok_total ? "fotos OK marcadas como NG (falsos NG)" : "Sin fotos OK apartadas: el umbral sale de las mismas fotos del entrenamiento."}
          </div>
        </div>
      </div>
      <Section title="Fotos NG" items={ng} onOpen={setLightbox} />
      <Section title="Fotos OK apartadas" items={ok} onOpen={setLightbox} />
      <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />
    </Card>
  );
}
